import { useCallback, useEffect, useMemo, useState } from "react";
import { api, esteFaraSemnal, mesajEroare } from "./api.js";
import { articoleCoada, operatiiCoada, pastreazaLocal } from "./offline/coada.js";
import { nomenclator, tineNomenclator } from "./offline/db.js";
import { um } from "./um.js";
import { STARI_VIZITA as ETICHETE_STATUS, TIPURI_FOTO, eticheta } from "./enums.js";
import { ChecklistTeren } from "./Mentenanta.jsx";
import { data, ora } from "./text.js";
import { tr } from "./i18n.js";

const PLANIFICARE = ["ADMIN_TENANT", "MANAGER_TEHNIC", "DISPECER", "COORDONATOR"];
const TEREN = [...PLANIFICARE, "TEHNICIAN"];
const STORNARE = ["ADMIN_TENANT", "MANAGER_TEHNIC"];
/**
 * Cine primește devizul — D14. Lista oglindește exact gărzile rutei din
 * backend; dacă cele două ar diverge, aici s-ar vedea un panou gol, nu date
 * interzise, fiindcă serverul rămâne autoritatea.
 */
const CU_DEVIZ = [
  "ADMIN_TENANT", "MANAGER_TEHNIC", "FINANCIAR", "MAGAZIONER", "AUDITOR",
  "CLIENT_ADMIN", "CLIENT_UTILIZATOR", "CLIENT_FINANCIAR",
];

/**
 * Valorile sunt exact cele din `schema.prisma` — nu o listă plauzibilă. Prima
 * variantă a acestui fișier inventase „Corectivă"/„Preventivă"/„Verificare",
 * pe care serverul le respingea; verificarea vizuală a prins-o, suita n-avea
 * cum: ea nu trece prin dropdownurile interfeței.
 */
const CATEGORII = [
  { cod: "PIF", titlu: "Punere în funcțiune" },
  { cod: "Reparatie", titlu: "Reparație" },
  { cod: "Mentenanta", titlu: "Mentenanță" },
  { cod: "Extindere", titlu: "Extindere" },
  { cod: "Scoatere", titlu: "Scoatere din funcțiune" },
];

const STARI_PLECARE = [
  { cod: "Functional", titlu: "Funcțional" },
  { cod: "FunctionalCuLimitari", titlu: "Funcțional, cu limitări" },
  { cod: "NefunctionalNecesitaRevenire", titlu: "Nefuncțional — necesită revenire" },
];

const ACTIUNI_DEFECT = [
  { cod: "Reparat", titlu: "Reparat" },
  { cod: "Inlocuit", titlu: "Înlocuit" },
  { cod: "RamaneDefect", titlu: "Rămâne defect" },
  { cod: "TrimisInRMA", titlu: "Trimis în RMA" },
];

/** Pașii din caietul de sarcini, în ordinea în care se parcurg pe teren. */
const PASI = [
  { cod: "checkin", titlu: "Check-in" },
  { cod: "constatari", titlu: "Constatări" },
  { cod: "operatii", titlu: "Operații" },
  { cod: "materiale", titlu: "Materiale" },
  { cod: "inchidere", titlu: "Închidere & semnătură" },
];

/*
 * Ce mai lipsește ca fișa să se poată valida, și pe ce PAS se completează.
 *
 * Regulile sunt cele din `finalizeazaFisa` (server) — copiate din serviciu, nu
 * scrise din memorie: două liste care trebuie să spună același lucru dar sunt
 * scrise separat ajung să nu-l mai spună. Divergența exista: interfața nu
 * cunoștea regula „remedierile sau măcar o operație", deci pe o fișă fără
 * niciuna butonul de validare arăta activ și serverul o refuza.
 *
 * Pasul contează fiindcă altfel „mai lipsesc constatările" apare abia pe pasul
 * cinci, adică după ce tehnicianul a trecut deja prin toate celelalte.
 */
export function lipsuriFisa(fisa, estimariRamase, stareLocala) {
  // Se traduce AICI, la apel, nu la definirea unei hărți: o hartă de literali
  // s-ar evalua o dată, la încărcarea modulului, iar comutatorul de limbă ar
  // schimba tot ecranul în afară de lista asta.
  const l = [];
  if (!fisa.oraStart) l.push({ pas: "checkin", text: tr("ora de început") });
  if (!fisa.oraFinish) l.push({ pas: "checkin", text: tr("ora de sfârșit") });
  if (!fisa.descriereConstatari) l.push({ pas: "constatari", text: tr("constatările") });
  if (!fisa.remedieriEfectuate && (fisa.operatii ?? []).length === 0) {
    l.push({ pas: "constatari", text: tr("remedierile sau măcar o operație") });
  }
  if (!fisa.stareLaPlecare && !stareLocala) {
    l.push({ pas: "inchidere", text: tr("starea la plecare") });
  }
  if (estimariRamase > 0) {
    // Șablon cu valoare, nu bucăți lipite: în engleză ordinea cuvintelor se
    // schimbă, iar două traduceri concatenate n-au cum s-o urmeze.
    l.push({
      pas: "operatii",
      text: estimariRamase === 1
        ? tr("timpul real pe {0} operație adusă de rețetă", estimariRamase)
        : tr("timpul real pe {0} operații aduse de rețetă", estimariRamase),
    });
  }
  return l;
}

/**
 * Enumerările vin din bază în forma lor tehnică — „InExecutie", „Facturabil".
 * Afișate așa, arată ca un scurgere de bază de date pe ecranul cuiva care
 * lucrează în română. Ce nu e în listă rămâne cum e: nu ascundem o valoare
 * nouă în spatele unei etichete inventate.
 */
const ETICHETE_ACOPERIRE = {
  Facturabil: "facturabil",
  InclusInContract: "inclus în contract",
  Garantie: "garanție",
};



/** „a", „a și b", „a, b și c" — nu „a b c". */
const enumera = (l) =>
  l.length <= 1 ? (l[0] ?? "") : `${l.slice(0, -1).join(", ")} și ${l[l.length - 1]}`;

/** Bani în formatul locului: virgulă zecimală, ca peste tot în aplicație. */
const lei = (v) => (v == null ? "—" :
  `${Number(v).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei`);
const pentruInput = (v) => {
  if (!v) return "";
  const d = new Date(v);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function Interventii({ eu }) {
  const [vizite, setVizite] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [tehnicieni, setTehnicieni] = useState([]);
  const [filtruClient, setFiltruClient] = useState("");
  const [filtruStatus, setFiltruStatus] = useState("");
  /*
   * Un tehnician care deschide ecranul vrea vizitele LUI, nu pe toate ale
   * firmei. Valoarea de pornire e „ale mele" pentru oricine are o fișă de
   * tehnician pe cont — nu pentru rolul TEHNICIAN: un coordonator care merge
   * și pe teren are aceeași nevoie, iar un cont cu rol de tehnician fără fișă
   * ar rămâne altfel cu o listă goală și fără explicație.
   */
  const [filtruTehnician, setFiltruTehnician] = useState(eu.tehnicianId ? "eu" : "");
  const [filtruDeLa, setFiltruDeLa] = useState("");
  const [filtruPanaLa, setFiltruPanaLa] = useState("");
  const [fisaId, setFisaId] = useState(null);
  const [formVizita, setFormVizita] = useState(false);
  const [checklist, setChecklist] = useState(null);
  const [incarca, setIncarca] = useState(false);
  const [eroare, setEroare] = useState("");
  // Lista se taie la un plafon. Tăcerea era chiar defectul: a 301-a vizită nu
  // exista, iar cine o căuta n-avea de unde ști de ce.
  const [taiat, setTaiat] = useState(null);

  const poatePlanifica = PLANIFICARE.includes(eu.rol);
  const poateLucra = TEREN.includes(eu.rol);

  useEffect(() => {
    api.clienti().then(setClienti).catch(() => {});
    // Lista se folosește doar ca filtru; dacă rolul n-are voie la ea, ecranul
    // rămâne întreg, doar fără dropdownul de tehnician.
    api.tehnicieni().then(setTehnicieni).catch(() => setTehnicieni([]));
  }, []);

  const reincarca = useCallback(async () => {
    setIncarca(true);
    try {
      const r = await api.vizite({
        client: filtruClient || undefined,
        status: filtruStatus || undefined,
        tehnician: filtruTehnician || undefined,
        deLa: filtruDeLa || undefined,
        panaLa: filtruPanaLa || undefined,
      });
      setVizite(r.vizite ?? []);
      setTaiat(r.total > (r.vizite?.length ?? 0) ? r : null);
    } catch (e) { setEroare(mesajEroare(e)); }
    finally { setIncarca(false); }
  }, [filtruClient, filtruStatus, filtruTehnician, filtruDeLa, filtruPanaLa]);

  useEffect(() => { reincarca(); }, [reincarca]);

  async function deschide(vizita) {
    setEroare("");
    if (vizita.fisa?.id) { setFisaId(vizita.fisa.id); return; }
    if (!poateLucra) return;
    try {
      const f = await api.deschideFisa(vizita.id);
      setFisaId(f.id);
      reincarca();
    } catch (e) { setEroare(mesajEroare(e)); }
  }

  if (fisaId) {
    return (
      <Fisa
        eu={eu}
        id={fisaId}
        laInchidere={() => { setFisaId(null); reincarca(); }}
        laSchimbareFisa={setFisaId}
      />
    );
  }

  return (
    <div className="continut">
      <h1>{tr("Intervenții")}</h1>
      <p className="subtitlu">
        {tr("Vizita programează lucrarea, fișa o duce până la semnătură. O fișă semnată nu se mai modifică — corectura se face prin stornare și o fișă nouă.")}
      </p>

      {/*
        Există DOUĂ drumuri către o vizită, iar cel de aici e al doilea. Când
        lucrarea pleacă de la o sesizare a clientului, vizita se naște singură
        din tichet: propui data, clientul o acceptă, vizita apare legată de
        tichet, iar ceasul de restaurare se pune singur în pauză cât timp
        aștepți răspunsul. O vizită creată de mână n-are tichet în spate, deci
        nimic din toate astea nu se întâmplă — și nimeni nu spunea asta.
      */}
      {poatePlanifica && (
        <p className="nota-cautare" style={{ marginTop: "-.4rem", marginBottom: "1rem" }}>
          {tr("Dacă lucrarea pleacă de la o sesizare a clientului, propune vizita din tichet: acolo se leagă singură de tichet și ține ceasul SLA. Vizita creată aici e pentru lucrările fără tichet.")}
        </p>
      )}

      <AprobareDeviz />

      {eroare && <div className="eroare">{eroare}</div>}

      <div className="panou">
        <div className="panou-antet">
          <span>{tr("Vizite")}</span>
          {poatePlanifica && (
            <button className="buton buton-mic buton-actiune" onClick={() => setFormVizita(true)}>
              {tr("vizită nouă")}
            </button>
          )}
        </div>
        <div className="panou-corp">
          <div className="formular-linie" style={{ gridTemplateColumns: "1fr 1fr", marginBottom: ".8rem" }}>
            <div className="camp" style={{ margin: 0 }}>
              <label htmlFor="f-tehnician">{tr("Cine")}</label>
              <select id="f-tehnician" value={filtruTehnician}
                onChange={(e) => setFiltruTehnician(e.target.value)}>
                {eu.tehnicianId && <option value="eu">{tr("ale mele")}</option>}
                <option value="">{tr("toți tehnicienii")}</option>
                {tehnicieni.map((t) => (
                  <option key={t.id} value={t.id}>{tr(t.numeComplet)}</option>
                ))}
              </select>
            </div>
            <div className="camp" style={{ margin: 0 }}>
              <label htmlFor="f-client">{tr("Client")}</label>
              <select id="f-client" value={filtruClient} onChange={(e) => setFiltruClient(e.target.value)}>
                <option value="">{tr("toți clienții")}</option>
                {clienti.map((c) => <option key={c.id} value={c.id}>{tr(c.denumire)}</option>)}
              </select>
            </div>
          </div>

          <div className="formular-linie" style={{ gridTemplateColumns: "1fr 1fr 1fr", marginBottom: ".6rem" }}>
            <div className="camp" style={{ margin: 0 }}>
              <label htmlFor="f-status">{tr("Stare")}</label>
              <select id="f-status" value={filtruStatus} onChange={(e) => setFiltruStatus(e.target.value)}>
                <option value="">{tr("toate")}</option>
                {["Programata", "InDeplasare", "InExecutie", "Suspendata", "Finalizata", "Anulata"]
                  .map((v) => <option key={v} value={v}>{tr(ETICHETE_STATUS[v])}</option>)}
              </select>
            </div>
            <div className="camp" style={{ margin: 0 }}>
              <label htmlFor="f-de-la">{tr("Programate de la")}</label>
              <input id="f-de-la" type="date" value={filtruDeLa}
                onChange={(e) => setFiltruDeLa(e.target.value)} />
            </div>
            <div className="camp" style={{ margin: 0 }}>
              <label htmlFor="f-pana-la">{tr("până la")}</label>
              <input id="f-pana-la" type="date" value={filtruPanaLa}
                onChange={(e) => setFiltruPanaLa(e.target.value)} />
            </div>
          </div>

          {/* Serverul acceptă intervalul de la început; lipsea doar felul de
              a-l cere fără să tastezi două date pentru întrebarea pusă cel mai
              des — „ce am azi". */}
          <div className="linie-actiune" style={{ marginBottom: "1rem" }}>
            <button className="buton buton-mic buton-sters" onClick={() => {
              const z = new Date().toISOString().slice(0, 10);
              setFiltruDeLa(z); setFiltruPanaLa(z);
            }}>{tr("azi")}</button>
            <button className="buton buton-mic buton-sters" onClick={() => {
              const a = new Date();
              const b = new Date(a.getTime() + 6 * 86400000);
              setFiltruDeLa(a.toISOString().slice(0, 10));
              setFiltruPanaLa(b.toISOString().slice(0, 10));
            }}>{tr("următoarele 7 zile")}</button>
            {(filtruDeLa || filtruPanaLa) && (
              <button className="buton buton-mic buton-discret" onClick={() => {
                setFiltruDeLa(""); setFiltruPanaLa("");
              }}>{tr("fără interval")}</button>
            )}
          </div>

          {taiat && (
            <p className="nota-cautare" style={{ marginBottom: ".6rem" }}>
              {tr("Se arată primele {0} vizite din {1}. Îngustează filtrul — pe client, pe stare sau pe interval — ca să le vezi pe celelalte.", taiat.plafon, taiat.total)}
            </p>
          )}

          {incarca ? (
            <div className="gol">{tr("Se încarcă…")}</div>
          ) : vizite.length === 0 ? (
            <div className="gol">{tr("Nicio vizită pentru filtrele alese.")}</div>
          ) : (
            <table className="tabel">
              <thead>
                <tr>
                  <th>{tr("Număr")}</th><th>{tr("Client")}</th><th>{tr("Locație")}</th><th>{tr("Categorie")}</th>
                  <th>{tr("Programată")}</th><th>{tr("Stare")}</th><th>{tr("Fișă")}</th><th />
                </tr>
              </thead>
              <tbody>
                {vizite.map((v) => (
                  <tr key={v.id}>
                    <td><span className="cod-mic">{v.numar}</span></td>
                    <td>{tr(v.client?.denumire) ?? "—"}</td>
                    <td>{tr(v.locatie?.denumire) ?? "—"}</td>
                    <td>{tr(CATEGORII.find((c) => c.cod === v.categorie)?.titlu) ?? v.categorie}</td>
                    <td>{data(v.dataProgramata)}{v.oraProgramata ? ` · ${v.oraProgramata}` : ""}</td>
                    <td><span className="pastila pastila-soft">{eticheta(ETICHETE_STATUS, v.status)}</span></td>
                    <td>{v.fisa?.numar ? <span className="cod-mic">{v.fisa.numar}</span> : <span className="fara-pret">—</span>}</td>
                    <td className="celula-actiuni">
                      {v.categorie === "Mentenanta" && (
                        <button className="buton buton-mic buton-actiune"
                          onClick={() => setChecklist(v.id)}>
                          {tr("checklist")}
                        </button>
                      )}
                      {v.fisa?.id ? (
                        <button className="buton buton-mic buton-sters" onClick={() => deschide(v)}>
                          {tr("deschide fișa")}
                        </button>
                      ) : poateLucra ? (
                        <button className="buton buton-mic buton-actiune" onClick={() => deschide(v)}>
                          check-in
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {checklist && (
        <ChecklistTeren
          eu={eu} vizitaId={checklist}
          laInchidere={() => { setChecklist(null); reincarca(); }}
        />
      )}

      {formVizita && (
        <FormVizita
          clienti={clienti}
          laRenuntare={() => setFormVizita(false)}
          laSalvare={() => { setFormVizita(false); reincarca(); }}
        />
      )}
    </div>
  );
}

// ── Vizită nouă ─────────────────────────────────────────────────────────────

/**
 * Comutatorul firmei: devizul unei fișe trece sau nu pe la aprobare înainte de
 * validare.
 *
 * Se arată DOAR celui care poate aproba — cine nu decide n-are ce face cu o
 * setare pe care oricum n-o poate schimba. Iar costul regulii se spune pe
 * față: cât timp o fișă așteaptă, stocul nu se scade și lucrarea nu se poate
 * preda la facturare.
 */
function AprobareDeviz() {
  const [stare, setStare] = useState(null);
  const [poate, setPoate] = useState(null);
  const [lucreaza, setLucreaza] = useState(false);
  const [eroare, setEroare] = useState("");

  useEffect(() => {
    api.drepturileMele()
      .then((d) => setPoate((d?.deviz ?? []).includes("aprobare")))
      .catch(() => setPoate(false));
  }, []);

  useEffect(() => {
    if (poate !== true) return;
    api.stareAprobareDeviz().then(setStare).catch((e) => setEroare(mesajEroare(e)));
  }, [poate]);

  if (poate !== true || !stare) return null;

  async function comuta() {
    setLucreaza(true); setEroare("");
    try { setStare(await api.setAprobareDeviz(!stare.activ)); }
    catch (e) { setEroare(mesajEroare(e)); }
    finally { setLucreaza(false); }
  }

  return (
    <div className="panou">
      <div className="panou-corp">
        {eroare && <div className="eroare">{eroare}</div>}
        <p className="nota-cautare" style={{ marginTop: 0 }}>
          {stare.activ
            ? tr("Nicio fișă nu se validează până nu aprobă cineva cu dreptul devizul ei. Cât timp așteaptă, stocul nu se scade și lucrarea nu se poate preda la facturare.")
            : tr("Fișele se validează direct, fără ca cineva să se uite peste deviz.")}
          {" "}
          <button className="buton-mic" disabled={lucreaza} onClick={comuta}>
            {stare.activ ? tr("nu mai cere aprobare") : tr("cere aprobare pe deviz")}
          </button>
        </p>
      </div>
    </div>
  );
}

function FormVizita({ clienti, laRenuntare, laSalvare }) {
  const [clientId, setClientId] = useState("");
  const [locatii, setLocatii] = useState([]);
  const [d, setD] = useState({
    locatieId: "", categorie: "Reparatie", scopInterventie: "",
    dataProgramata: new Date().toISOString().slice(0, 10), oraProgramata: "",
  });
  const [eroare, setEroare] = useState("");
  const [salveaza, setSalveaza] = useState(false);

  useEffect(() => {
    if (!clientId) { setLocatii([]); return; }
    api.locatii(clientId).then(setLocatii).catch((e) => setEroare(mesajEroare(e)));
  }, [clientId]);

  async function trimite(e) {
    e.preventDefault();
    setSalveaza(true); setEroare("");
    try {
      await api.creeazaVizita({
        clientId,
        locatieId: d.locatieId,
        categorie: d.categorie,
        scopInterventie: d.scopInterventie,
        dataProgramata: new Date(d.dataProgramata).toISOString(),
        ...(d.oraProgramata ? { oraProgramata: d.oraProgramata } : {}),
      });
      laSalvare();
    } catch (er) { setEroare(mesajEroare(er)); }
    finally { setSalveaza(false); }
  }

  return (
    <div className="modal-fundal" onClick={laRenuntare}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{tr("Vizită nouă")}</h2>
        <p className="subtitlu">{tr("Când, la cine, pentru ce. Fișa se deschide la fața locului.")}</p>
        {eroare && <div className="eroare">{eroare}</div>}
        <form onSubmit={trimite}>
          <div className="camp">
            <label htmlFor="v-client">{tr("Client")}</label>
            <select id="v-client" value={clientId} required onChange={(e) => setClientId(e.target.value)}>
              <option value="">{tr("alege…")}</option>
              {clienti.map((c) => <option key={c.id} value={c.id}>{tr(c.denumire)}</option>)}
            </select>
          </div>
          <div className="camp">
            <label htmlFor="v-locatie">{tr("Locație")}</label>
            <select
              id="v-locatie" value={d.locatieId} required disabled={!clientId || locatii.length === 0}
              onChange={(e) => setD({ ...d, locatieId: e.target.value })}
            >
              <option value="">{clientId ? "alege…" : tr("alege întâi clientul")}</option>
              {locatii.map((l) => <option key={l.id} value={l.id}>{tr(l.denumire)}</option>)}
            </select>
            {/* Un select gol, fără explicație, arată ca o aplicație stricată.
                Clientul chiar n-are locații — spunem asta și unde se rezolvă. */}
            {clientId && locatii.length === 0 && (
              <span className="nota-cautare">
                {tr("Clientul n-are nicio locație activă. Adaugă una din secțiunea Clienți — o vizită se programează la o adresă, nu la o firmă.")}
              </span>
            )}
          </div>
          <div className="camp">
            <label htmlFor="v-categorie">{tr("Categorie")}</label>
            <select id="v-categorie" value={d.categorie} onChange={(e) => setD({ ...d, categorie: e.target.value })}>
              {CATEGORII.map((c) => <option key={c.cod} value={c.cod}>{tr(c.titlu)}</option>)}
            </select>
          </div>
          <div className="camp">
            <label htmlFor="v-scop">{tr("Scopul intervenției")}</label>
            <input
              id="v-scop" value={d.scopInterventie} required minLength={5}
              placeholder={tr("ex. cameră cu imagine cețoasă la poarta 2")}
              onChange={(e) => setD({ ...d, scopInterventie: e.target.value })}
            />
          </div>
          <div className="formular-linie" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div className="camp">
              <label htmlFor="v-data">{tr("Data")}</label>
              <input
                id="v-data" type="date" value={d.dataProgramata} required
                onChange={(e) => setD({ ...d, dataProgramata: e.target.value })}
              />
            </div>
            <div className="camp">
              <label htmlFor="v-ora">{tr("Ora (opțional)")}</label>
              <input
                id="v-ora" type="time" value={d.oraProgramata}
                onChange={(e) => setD({ ...d, oraProgramata: e.target.value })}
              />
            </div>
          </div>
          <div className="modal-actiuni">
            <button type="button" className="buton buton-mic buton-sters" onClick={laRenuntare}>{tr("renunță")}</button>
            <button type="submit" className="buton buton-mic buton-actiune" disabled={salveaza}>
              {salveaza ? tr("se creează…") : tr("creează vizita")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Fișa de intervenție, în cinci pași ──────────────────────────────────────

function Fisa({ eu, id, laInchidere, laSchimbareFisa }) {
  const [fisa, setFisa] = useState(null);
  const [pas, setPas] = useState("checkin");
  const [eroare, setEroare] = useState("");
  const [mesaj, setMesaj] = useState("");
  const [lucreaza, setLucreaza] = useState(false);
  /*
   * MT19 — „telefonul nu ajunge la server ACUM".
   *
   * Nu se citește doar din `navigator.onLine`: o hală cu semnal slab arată
   * „online" și tot nu răspunde nimeni. Se aprinde la prima salvare picată din
   * lipsă de semnal și se stinge la prima care chiar ajunge.
   */
  const [faraSemnal, setFaraSemnal] = useState(!navigator.onLine);

  useEffect(() => {
    const iese = () => setFaraSemnal(true);
    window.addEventListener("offline", iese);
    return () => window.removeEventListener("offline", iese);
  }, []);

  const poateLucra = TEREN.includes(eu.rol);
  const poateStorna = STORNARE.includes(eu.rol);
  const poateVedeaDeviz = CU_DEVIZ.includes(eu.rol);

  const reincarca = useCallback(async () => {
    try { setFisa(await api.fisa(id)); }
    catch (e) { setEroare(mesajEroare(e)); }
  }, [id]);

  useEffect(() => { reincarca(); }, [reincarca]);

  /**
   * Orice acțiune trece pe aici: un singur loc care ține eroarea și reîncarcă.
   *
   * MT19 — și un singur loc pentru plasa de teren. Când salvarea pică din lipsă
   * de SEMNAL (nu dintr-un refuz al serverului), munca nu se pierde: fișa, așa
   * cum o știe serverul plus schimbarea care n-a apucat să plece, intră în coada
   * din telefon și urcă singură când revine semnalul. Omul vede o notă calmă,
   * nu o bandă roșie — n-a greșit nimic, doar e într-o hală.
   *
   * `schimbare` descrie ce se încerca, în vocabularul cozii. Fără ea plasa tot
   * prinde fișa, dar fără ultima modificare — de-aia o dau la pașii unde omul
   * chiar scrie ceva.
   */
  const fa = useCallback(async (actiune, dupa, schimbare) => {
    setLucreaza(true); setEroare(""); setMesaj("");
    try {
      const r = await actiune();
      setFaraSemnal(false);
      await reincarca();
      if (dupa) dupa(r);
      return r;
    } catch (e) {
      if (esteFaraSemnal(e) && fisa) {
        setFaraSemnal(true);
        try {
          await pastreazaLocal(fisa, schimbare ?? {});
          setMesaj(tr("Nu e semnal. Ce ai lucrat s-a salvat în telefon și pleacă singur când revine semnalul."));
          return null;
        } catch {
          // Nici local nu s-a putut salva (fereastră privată, date blocate):
          // atunci chiar e o problemă, iar omul trebuie s-o afle pe loc.
          setEroare(tr("Nu e semnal, iar telefonul nu poate ține datele local. Nu pleca de la obiectiv fără să salvezi."));
          return null;
        }
      }
      setEroare(mesajEroare(e));
      return null;
    }
    finally { setLucreaza(false); }
  }, [reincarca, fisa]);

  if (!fisa) {
    return (
      <div className="continut">
        {eroare ? <div className="eroare">{eroare}</div> : <div className="gol">{tr("Se încarcă fișa…")}</div>}
        <button className="buton buton-mic buton-sters" onClick={laInchidere}>{tr("înapoi la vizite")}</button>
      </div>
    );
  }

  const editabila = fisa.esteEditabila && poateLucra;
  const estimariRamase = (fisa.operatii ?? []).filter((o) => o.timpEstimat).length;
  // Calculată aici, nu în pasul de închidere: taburile au nevoie de ea.
  // `stareLaPlecare` aleasă dar nesalvată încă rămâne o stare locală a acelui
  // pas — de-aia lista de aici o poate supraevalua cu un element până la
  // salvare, iar pasul de închidere o recalculează cu starea lui.
  const lipsuri = lipsuriFisa(fisa, estimariRamase, null);

  return (
    <div className="continut">
      <div className="linie-actiune" style={{ marginBottom: ".6rem" }}>
        <button className="buton buton-mic buton-sters" onClick={laInchidere}>{tr("← vizite")}</button>
        <span className="cod-mic">{fisa.numar}</span>
        <span className={"pastila " + (fisa.status === "Semnata" ? "pastila-hard" : "pastila-soft")}>
          {eticheta(ETICHETE_STATUS, fisa.status)}
        </span>
      </div>

      <h1>{tr(fisa.vizita?.client?.denumire)}</h1>
      <p className="subtitlu">
        {tr(fisa.vizita?.locatie?.denumire)}
        {fisa.vizita?.locatie?.adresa ? ` · ${fisa.vizita.locatie.adresa}` : ""}
        {" · "}{tr(CATEGORII.find((c) => c.cod === fisa.vizita?.categorie)?.titlu) ?? fisa.vizita?.categorie}
        {" · "}{fisa.vizita?.scopInterventie}
      </p>

      {eroare && <div className="eroare">{eroare}</div>}
      {mesaj && <div className="panou panou-atentie"><div className="panou-corp">{mesaj}</div></div>}

      {!fisa.esteEditabila && (
        <div className="panou panou-atentie">
          <div className="panou-corp">
            {fisa.status === "Semnata"
              ? tr("Fișa e semnată. Nu se mai modifică — corectura se face prin stornare și o fișă nouă.")
              : fisa.status === "Stornata"
                ? tr("Fișa e stornată. A fost înlocuită de o fișă nouă.")
                : tr("Fișa e validată. Doar semnarea mai e posibilă.")}
          </div>
        </div>
      )}

      <div className="taburi-sisteme" style={{ marginBottom: "1rem" }}>
        {PASI.map((p, i) => {
          const alePasului = lipsuri.filter((x) => x.pas === p.cod);
          return (
            <button
              key={p.cod}
              className={"tab-sistem" + (p.cod === pas ? " activ" : "")}
              // Mesajul e despre ce s-a întâmplat la pasul curent; dus mai
              // departe, ar descrie un ecran pe care nu-l mai are în față.
              onClick={() => { setPas(p.cod); setMesaj(""); setEroare(""); }}
              title={alePasului.length
                ? `${tr("Mai lipsesc:")} ${alePasului.map((x) => x.text).join(", ")}.`
                : undefined}
            >
              <span className="tab-numar">{i + 1}</span> {tr(p.titlu)}
              {editabila && alePasului.length > 0 && (
                <span className="numar numar-lipsa">{alePasului.length}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Ce mai lipsește se spune de la primul pas, nu abia la închidere: altfel
          tehnicianul află că i-au lipsit constatările după ce a trecut prin
          toate cele cinci ecrane. */}
      {editabila && lipsuri.length > 0 && (
        <p className="subtitlu" style={{ marginTop: "-.6rem" }}>
          {tr("Ca să poți valida fișa, mai lipsesc:")}{" "}
          {lipsuri.map((x) => x.text).join(", ")}.
        </p>
      )}

      {pas === "checkin" && <PasCheckin fisa={fisa} editabila={editabila} fa={fa} lucreaza={lucreaza} />}
      {pas === "constatari" && (
        <>
          <PasConstatari fisa={fisa} editabila={editabila} fa={fa} lucreaza={lucreaza} />
          <PasFotografii fisa={fisa} editabila={editabila}
            laSchimbare={() => reincarca()} />
        </>
      )}
      {pas === "operatii" && (
        <PasOperatii
          fisa={fisa} editabila={editabila} fa={fa} lucreaza={lucreaza}
          laMesaj={setMesaj} estimariRamase={estimariRamase} utilizatorId={eu.id}
        />
      )}
      {pas === "materiale" && (
        <PasMateriale fisa={fisa} editabila={editabila} fa={fa} lucreaza={lucreaza}
          utilizatorId={eu.id} />
      )}
      {pas === "inchidere" && (
        <PasInchidere
          fisa={fisa} editabila={editabila} fa={fa} lucreaza={lucreaza}
          poateVedeaDeviz={poateVedeaDeviz} poateStorna={poateStorna}
          estimariRamase={estimariRamase} laSchimbareFisa={laSchimbareFisa}
          faraSemnal={faraSemnal}
        />
      )}
    </div>
  );
}

// ── Pasul 1: check-in ───────────────────────────────────────────────────────

function PasCheckin({ fisa, editabila, fa, lucreaza }) {
  const [d, setD] = useState({
    oraSosire: pentruInput(fisa.oraSosire),
    oraStart: pentruInput(fisa.oraStart),
    oraFinish: pentruInput(fisa.oraFinish),
    durataDeplasareMinute: fisa.durataDeplasareMinute ?? "",
    kmParcursi: fisa.kmParcursi ?? "",
  });

  async function salveaza() {
    // Aceleași câmpuri pleacă la server ȘI, dacă nu e semnal, rămân în coadă:
    // vocabularul cozii e chiar vocabularul fișei, deci nu se traduce nimic.
    const dto = {
      ...(d.oraSosire ? { oraSosire: new Date(d.oraSosire).toISOString() } : {}),
      ...(d.oraStart ? { oraStart: new Date(d.oraStart).toISOString() } : {}),
      ...(d.oraFinish ? { oraFinish: new Date(d.oraFinish).toISOString() } : {}),
      ...(d.durataDeplasareMinute !== "" ? { durataDeplasareMinute: Number(d.durataDeplasareMinute) } : {}),
      ...(d.kmParcursi !== "" ? { kmParcursi: Number(d.kmParcursi) } : {}),
    };
    await fa(() => api.oreFisa(fisa.id, dto), null, dto);
  }

  return (
    <div className="panou">
      <div className="panou-antet">
        <span>{tr("Check-in și timpi")}</span>
        {fisa.oreIntroduseManual && (
          <span className="marcaj">{tr("ore introduse manual")}</span>
        )}
      </div>
      <div className="panou-corp">
        <p className="nota-cautare" style={{ marginTop: 0, marginBottom: "1rem" }}>
          {tr("Ora de sosire s-a pus singură la check-in. Orice oră trecută de mână se marchează ca atare — cronometrul e real sau declarativ, iar diferența trebuie să rămână vizibilă în audit.")}
        </p>

        <div className="formular-linie" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
          {[
            ["oraSosire", "Sosire"],
            ["oraStart", "Început lucru"],
            ["oraFinish", "Sfârșit lucru"],
          ].map(([cheie, titlu]) => (
            <div className="camp" key={cheie}>
              <label htmlFor={`c-${cheie}`}>{titlu}</label>
              <input
                id={`c-${cheie}`} type="datetime-local" value={d[cheie]} disabled={!editabila}
                onChange={(e) => setD({ ...d, [cheie]: e.target.value })}
              />
            </div>
          ))}
        </div>

        <div className="formular-linie" style={{ gridTemplateColumns: "1fr 1fr" }}>
          <div className="camp">
            <label htmlFor="c-deplasare">{tr("Deplasare (minute)")}</label>
            <input
              id="c-deplasare" type="number" min="0" value={d.durataDeplasareMinute} disabled={!editabila}
              onChange={(e) => setD({ ...d, durataDeplasareMinute: e.target.value })}
            />
          </div>
          <div className="camp">
            <label htmlFor="c-km">{tr("Kilometri parcurși")}</label>
            <input
              id="c-km" type="number" min="0" value={d.kmParcursi} disabled={!editabila}
              onChange={(e) => setD({ ...d, kmParcursi: e.target.value })}
            />
          </div>
        </div>

        {fisa.durataMinute != null && (
          <p className="nota-cautare">
            {tr("Durata calculată la validare:")} <strong>{fisa.durataMinute} {tr("de minute")}</strong>.
          </p>
        )}

        {editabila && (
          <div className="linie-actiune">
            <button className="buton buton-mic buton-actiune" onClick={salveaza} disabled={lucreaza}>
              {tr("salvează timpii")}
            </button>
            <button
              className="buton buton-mic buton-sters" disabled={lucreaza}
              onClick={() => fa(() => api.opresteCronometrul(fisa.id))}
            >
              {tr("oprește cronometrul acum")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Pasul 2: constatări ─────────────────────────────────────────────────────

function PasConstatari({ fisa, editabila, fa, lucreaza }) {
  const [d, setD] = useState({
    descriereConstatari: fisa.descriereConstatari ?? "",
    remedieriEfectuate: fisa.remedieriEfectuate ?? "",
    cauzaRadacina: fisa.cauzaRadacina ?? "",
  });
  const [formDefect, setFormDefect] = useState(false);

  return (
    <>
      <div className="panou">
        <div className="panou-antet"><span>{tr("Ce s-a găsit, ce s-a făcut")}</span></div>
        <div className="panou-corp">
          <div className="camp">
            <label htmlFor="k-constatari">{tr("Constatări")}</label>
            <textarea
              id="k-constatari" rows={3} className="camp-textarea" value={d.descriereConstatari}
              disabled={!editabila} placeholder={tr("Ce ai găsit la fața locului.")}
              onChange={(e) => setD({ ...d, descriereConstatari: e.target.value })}
            />
          </div>
          <div className="camp">
            <label htmlFor="k-remedieri">{tr("Remedieri efectuate")}</label>
            <textarea
              id="k-remedieri" rows={3} className="camp-textarea" value={d.remedieriEfectuate}
              disabled={!editabila} placeholder={tr("Ce ai făcut concret.")}
              onChange={(e) => setD({ ...d, remedieriEfectuate: e.target.value })}
            />
          </div>
          <div className="camp">
            <label htmlFor="k-cauza">{tr("Cauza rădăcină (opțional)")}</label>
            <textarea
              id="k-cauza" rows={2} className="camp-textarea" value={d.cauzaRadacina}
              disabled={!editabila} placeholder={tr("De ce s-a întâmplat, dacă se poate spune.")}
              onChange={(e) => setD({ ...d, cauzaRadacina: e.target.value })}
            />
          </div>
          {editabila && (
            <div className="linie-actiune">
              <button
                className="buton buton-mic buton-actiune" disabled={lucreaza}
                onClick={() => {
                  const dto = {
                    ...(d.descriereConstatari ? { descriereConstatari: d.descriereConstatari } : {}),
                    ...(d.remedieriEfectuate ? { remedieriEfectuate: d.remedieriEfectuate } : {}),
                    ...(d.cauzaRadacina ? { cauzaRadacina: d.cauzaRadacina } : {}),
                  };
                  return fa(() => api.continutFisa(fisa.id, dto), null, dto);
                }}
              >
                {tr("salvează")}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="panou">
        <div className="panou-antet">
          <span>{tr("Echipamente defecte")} <span className="numar">{fisa.defecte?.length ?? 0}</span></span>
          {editabila && (
            <button className="buton buton-mic buton-actiune" onClick={() => setFormDefect(true)}>
              {tr("adaugă")}
            </button>
          )}
        </div>
        <div className="panou-corp">
          {(fisa.defecte ?? []).length === 0 ? (
            <div className="gol">{tr("Niciun echipament raportat defect.")}</div>
          ) : (
            <table className="tabel">
              <thead>
                <tr>
                  <th>{tr("Echipament")}</th><th>{tr("Defect")}</th><th>{tr("Acțiune")}</th>
                  {editabila && <th />}
                </tr>
              </thead>
              <tbody>
                {fisa.defecte.map((x) => (
                  <tr key={x.id}>
                    <td>
                      {x.echipament?.eticheta ?? x.tipComponenta?.denumire ?? "—"}
                      {x.echipament?.codQR && <span className="cod-mic" style={{ marginLeft: ".4rem" }}>{x.echipament.codQR}</span>}
                    </td>
                    <td>{x.descriereDefect}</td>
                    <td>{tr(ACTIUNI_DEFECT.find((a) => a.cod === x.actiune)?.titlu) ?? x.actiune}</td>
                    {editabila && (
                      <td className="celula-actiuni">
                        <button
                          className="buton buton-mic buton-sters" disabled={lucreaza}
                          onClick={() => fa(() => api.stergeLinieFisa(fisa.id, "defecte", x.id))}
                        >
                          {tr("scoate")}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {formDefect && (
        <FormDefect
          fisa={fisa}
          laRenuntare={() => setFormDefect(false)}
          laSalvare={async (dto) => { await fa(() => api.adaugaDefectFisa(fisa.id, dto)); setFormDefect(false); }}
        />
      )}
    </>
  );
}

function FormDefect({ fisa, laRenuntare, laSalvare }) {
  const [cauta, setCauta] = useState("");
  const [rezultate, setRezultate] = useState([]);
  const [ales, setAles] = useState(null);
  const [tipuri, setTipuri] = useState([]);
  const [tipComponentaId, setTipComponentaId] = useState("");
  const [d, setD] = useState({ descriereDefect: "", actiune: "Reparat" });

  useEffect(() => {
    if (cauta.trim().length < 2) { setRezultate([]); return; }
    let anulat = false;
    api.cautaEchipament(cauta.trim(), fisa.vizita?.client?.id)
      .then((r) => { if (!anulat) setRezultate(r.slice(0, 8)); })
      .catch(() => {});
    return () => { anulat = true; };
  }, [cauta, fisa]);

  // Lista de tipuri e drumul de rezervă: o piesă care nu figurează în parc tot
  // trebuie să poată fi raportată, altfel constatarea se pierde exact în cazul
  // în care parcul e incomplet — adică tocmai când contează.
  useEffect(() => {
    api.arbore()
      .then((sisteme) => setTipuri(
        sisteme.flatMap((sis) => sis.subsisteme.flatMap((sub) =>
          sub.tipuriComponenta.map((t) => ({
            id: t.id, denumire: t.denumire, context: `${sis.denumire} · ${sub.denumire}`,
          })))),
      ))
      .catch(() => {});
  }, []);

  return (
    <div className="modal-fundal" onClick={laRenuntare}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{tr("Echipament defect")}</h2>
        <p className="subtitlu">
          {tr("Caută-l în parcul clientului. Dacă nu e inventariat, lasă căutarea goală și descrie-l — constatarea nu se pierde din lipsa unui rând în parc.")}
        </p>
        <div className="camp">
          <label htmlFor="df-cauta">{tr("Echipament din parc")}</label>
          <input
            id="df-cauta" value={ales ? ales.eticheta : cauta}
            placeholder={tr("etichetă, cod QR sau serie")}
            onChange={(e) => { setAles(null); setCauta(e.target.value); }}
          />
        </div>
        {!ales && rezultate.length > 0 && (
          <ul className="lista-rezultate">
            {rezultate.map((r) => (
              <li key={r.id}>
                <button className="buton buton-mic buton-sters" onClick={() => { setAles(r); setRezultate([]); }}>
                  {r.eticheta}
                </button>
                <span className="rezultat-context">{tr(r.locatie?.denumire) ?? ""}</span>
              </li>
            ))}
          </ul>
        )}
        {!ales && (
          <div className="camp">
            <label htmlFor="df-tip">{tr("…sau tipul componentei, dacă nu e în parc")}</label>
            <select id="df-tip" value={tipComponentaId} onChange={(e) => setTipComponentaId(e.target.value)}>
              <option value="">{tr("alege…")}</option>
              {tipuri.map((t) => (
                <option key={t.id} value={t.id}>{tr(t.denumire)} — {t.context}</option>
              ))}
            </select>
          </div>
        )}

        <div className="camp">
          <label htmlFor="df-descriere">{tr("Ce e defect")}</label>
          <textarea
            id="df-descriere" rows={2} className="camp-textarea" value={d.descriereDefect}
            onChange={(e) => setD({ ...d, descriereDefect: e.target.value })}
          />
        </div>
        <div className="camp">
          <label htmlFor="df-actiune">{tr("Ce s-a făcut")}</label>
          <select id="df-actiune" value={d.actiune} onChange={(e) => setD({ ...d, actiune: e.target.value })}>
            {ACTIUNI_DEFECT.map((a) => <option key={a.cod} value={a.cod}>{tr(a.titlu)}</option>)}
          </select>
        </div>
        <div className="modal-actiuni">
          <button className="buton buton-mic buton-sters" onClick={laRenuntare}>{tr("renunță")}</button>
          <button
            className="buton buton-mic buton-actiune"
            disabled={d.descriereDefect.trim().length < 5 || (!ales && !tipComponentaId)}
            onClick={() => laSalvare({
              ...(ales ? { echipamentId: ales.id } : { tipComponentaId }),
              descriereDefect: d.descriereDefect,
              actiune: d.actiune,
            })}
          >
            {tr("adaugă")}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * O listă de nomenclator, adusă de la server când e semnal și din telefon când
 * nu e.
 *
 * Fără asta, pasul de operații arăta un dropdown gol în hală — și nu spunea de
 * ce. Iar un dropdown gol se citește ca „firma n-are nicio operație", nu ca
 * „n-am putut întreba". Întoarce și dacă lista vine din telefon, ca ecranul
 * s-o poată spune.
 */
function useNomenclator(nume, cheama, utilizatorId) {
  const [lista, setLista] = useState([]);
  const [dinTelefon, setDinTelefon] = useState(false);
  /*
   * Copia poartă în cheie UTILIZATORUL care a adus-o.
   *
   * Serverul îngustează listele după rol — un tehnician nu vede toate
   * gestiunile. Pe un telefon al firmei, folosit de mai mulți oameni, o cheie
   * comună ar servi celui de-al doilea copia primului, adică exact ce garda de
   * pe server refuzase. Copia altcuiva pur și simplu nu se găsește.
   */
  const cheie = `${utilizatorId ?? "necunoscut"}:${nume}`;

  useEffect(() => {
    let viu = true;
    cheama()
      .then((r) => {
        if (!viu) return;
        setLista(r); setDinTelefon(false);
        tineNomenclator(cheie, r);
      })
      .catch(async () => {
        const local = await nomenclator(cheie);
        if (!viu || !local) return;
        setLista(local); setDinTelefon(true);
      });
    return () => { viu = false; };
    // Cheia identifică lista ȘI omul. `cheama` e rescrisă la fiecare randare,
    // deci a pune-o în dependențe ar reîncărca nomenclatorul la fiecare tastă.
  }, [cheie]);

  return [lista, dinTelefon];
}

// ── Pasul 3: operații ───────────────────────────────────────────────────────

function PasOperatii({ fisa, editabila, fa, lucreaza, laMesaj, estimariRamase, utilizatorId }) {
  const [operatii, operatiiDinTelefon] = useNomenclator(
    "operatii", () => api.operatii({ toate: true }), utilizatorId);
  const [alesId, setAlesId] = useState("");
  const [minute, setMinute] = useState("");
  const [retete, setRetete] = useState([]);
  const [retetaId, setRetetaId] = useState("");
  const [retetaDeschisa, setRetetaDeschisa] = useState(null);

  useEffect(() => {
    // Rețetele NU se țin în telefon: aplicarea uneia cere oricum serverul.
    api.retete({}).then(setRetete).catch(() => { /* clientul n-are voie: normal */ });
  }, []);

  async function aplicaReteta(optionale) {
    const r = await fa(() => api.aplicaReteta(fisa.id, { retetaId, ...(optionale ? { optionale } : {}) }));
    setRetetaDeschisa(null);
    if (!r) return;
    const parti = [];
    if (r.operatii.adaugate) parti.push(`${r.operatii.adaugate} operații adăugate`);
    if (r.operatii.cumulate) parti.push(`${r.operatii.cumulate} cumulate peste cele existente`);
    if (r.articole.adaugate) parti.push(`${r.articole.adaugate} piese adăugate`);
    const sarite = [...r.operatii.sarite, ...r.articole.sarite];
    if (sarite.length) parti.push(`${sarite.length} sărite (${sarite.map((x) => x.denumire).join(", ")})`);
    if (r.deCompletat.length) {
      parti.push(`de completat seria pentru: ${r.deCompletat.map((x) => x.articol).join(", ")}`);
    }
    // O rețetă goală n-a adus nimic. Fără ramura asta, mesajul se termina cu o
    // liniuță și nimic după ea — arată a interfață stricată, nu a rețetă goală.
    laMesaj(parti.length
      ? `Rețeta „${r.reteta.denumire}" aplicată — ${parti.join("; ")}.`
      : `Rețeta „${r.reteta.denumire}" n-a adus nimic pe fișă: nu are nicio linie obligatorie.`);
  }

  return (
    <>
      {operatiiDinTelefon && (
        <p className="nota">{tr("Lista vine din telefon, copiată la ultima trecere cu semnal. Ce e adăugat de atunci în birou nu se vede aici.")}</p>
      )}
      {estimariRamase > 0 && (
        <div className="panou panou-atentie">
          <div className="panou-corp">
            <strong>{estimariRamase} {estimariRamase === 1 ? tr("operație are") : tr("operații au")} {tr("timpul luat din normă")}</strong>{tr(", nu măsurat. Treci timpul real peste el — fișa nu se validează altfel.")}
          </div>
        </div>
      )}

      {editabila && retete.length > 0 && (
        <div className="panou">
          <div className="panou-antet"><span>{tr("Pornește de la o rețetă")}</span></div>
          <div className="panou-corp">
            <p className="nota-cautare" style={{ marginTop: 0, marginBottom: ".8rem" }}>
              {tr("Rețeta populează fișa, nu o blochează: tot ce aduce rămâne editabil și ștergibil. Se pot aplica mai multe — liniile identice se cumulează.")}
            </p>
            <div className="linie-actiune">
              <select value={retetaId} onChange={(e) => setRetetaId(e.target.value)} style={{ flex: 1 }}>
                <option value="">{tr("alege o rețetă…")}</option>
                {retete.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.cod} — {tr(r.denumire)} ({r.numarOperatii} {tr("operații,")} {r.numarArticole} {tr("piese)")}
                  </option>
                ))}
              </select>
              <button
                className="buton buton-mic buton-actiune" disabled={!retetaId || lucreaza}
                onClick={async () => {
                  const r = await api.reteta(retetaId).catch(() => null);
                  const areOptionale = r && [...r.operatii, ...r.articole].some((l) => l.optionala);
                  if (areOptionale) setRetetaDeschisa(r); else aplicaReteta(null);
                }}
              >
                {tr("aplică")}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="panou">
        <div className="panou-antet">
          <span>{tr("Operații executate")} <span className="numar">{fisa.operatii?.length ?? 0}</span></span>
        </div>
        <div className="panou-corp">
          {(fisa.operatii ?? []).length === 0 ? (
            <div className="gol">{tr("Nicio operație trecută pe fișă.")}</div>
          ) : (
            <table className="tabel">
              <thead>
                <tr>
                  <th>{tr("Operație")}</th><th>{tr("Cantitate")}</th><th>{tr("Timp real")}</th>
                  {editabila && <th />}
                </tr>
              </thead>
              <tbody>
                {fisa.operatii.map((o) => (
                  <LinieOperatie
                    key={o.id} linie={o} fisa={fisa} editabila={editabila} fa={fa} lucreaza={lucreaza}
                  />
                ))}
              </tbody>
            </table>
          )}

          {editabila && (
            <div className="linie-actiune" style={{ marginTop: "1rem" }}>
              <select value={alesId} onChange={(e) => setAlesId(e.target.value)} style={{ flex: 1 }}>
                <option value="">{tr("adaugă o operație…")}</option>
                {operatii.map((o) => (
                  <option key={o.id} value={o.id}>{o.cod} — {tr(o.denumire)}</option>
                ))}
              </select>
              <input
                type="number" min="1" placeholder={tr("minute")} value={minute} style={{ width: "7rem" }}
                onChange={(e) => setMinute(e.target.value)}
              />
              <button
                className="buton buton-mic buton-actiune"
                disabled={!alesId || !minute || lucreaza}
                onClick={async () => {
                  const linie = { operatieId: alesId, timpRealMinute: Number(minute) };
                  await fa(
                    () => api.adaugaOperatieFisa(fisa.id, linie),
                    null,
                    { operatii: [...operatiiCoada(fisa), { ...linie, cantitate: 1 }] },
                  );
                  setAlesId(""); setMinute("");
                }}
              >
                {tr("adaugă")}
              </button>
            </div>
          )}
        </div>
      </div>

      {retetaDeschisa && (
        <AlegeOptionale
          reteta={retetaDeschisa}
          laRenuntare={() => setRetetaDeschisa(null)}
          laAplicare={aplicaReteta}
        />
      )}
    </>
  );
}

function LinieOperatie({ linie, fisa, editabila, fa, lucreaza }) {
  const [minute, setMinute] = useState(String(linie.timpRealMinute ?? ""));
  const [editez, setEditez] = useState(false);

  return (
    <tr className={linie.timpEstimat ? "linie-estimata" : undefined}>
      <td>
        {tr(linie.operatie?.denumire)}
        <span className="cod-mic" style={{ marginLeft: ".4rem" }}>{linie.operatie?.cod}</span>
      </td>
      <td>{Number(linie.cantitate)}</td>
      <td>
        {editez ? (
          <input
            type="number" min="1" value={minute} style={{ width: "6rem" }}
            onChange={(e) => setMinute(e.target.value)}
          />
        ) : (
          <>
            {linie.timpRealMinute} {tr("min")}
            {linie.timpEstimat && <span className="marcaj">{tr("din normă — de confirmat")}</span>}
          </>
        )}
      </td>
      {editabila && (
      <td className="celula-actiuni">
        {(editez ? (
          <>
            <button
              className="buton buton-mic buton-actiune" disabled={lucreaza || !minute}
              onClick={async () => {
                await fa(
                  () => api.actualizeazaOperatieFisa(fisa.id, linie.id, { timpRealMinute: Number(minute) }),
                  null,
                  {
                    operatii: operatiiCoada(fisa).map((o, i) => (
                      fisa.operatii[i].id === linie.id ? { ...o, timpRealMinute: Number(minute) } : o
                    )),
                  },
                );
                setEditez(false);
              }}
            >
              {tr("salvează")}
            </button>
            <button className="buton buton-mic buton-sters" onClick={() => setEditez(false)}>{tr("renunță")}</button>
          </>
        ) : (
          <>
            <button className="buton buton-mic buton-sters" onClick={() => setEditez(true)}>
              {linie.timpEstimat ? tr("pune timpul real") : tr("schimbă timpul")}
            </button>
            <button
              className="buton buton-mic buton-sters" disabled={lucreaza}
              onClick={() => fa(
                () => api.stergeLinieFisa(fisa.id, "operatii", linie.id),
                null,
                { operatii: operatiiCoada(fisa).filter((_, i) => fisa.operatii[i].id !== linie.id) },
              )}
            >
              {tr("scoate")}
            </button>
          </>
        ))}
      </td>
      )}
    </tr>
  );
}

function AlegeOptionale({ reteta, laRenuntare, laAplicare }) {
  const optionale = useMemo(
    () => [
      ...reteta.operatii.filter((l) => l.optionala).map((l) => ({ id: l.id, denumire: l.operatie.denumire, fel: "operație" })),
      ...reteta.articole.filter((l) => l.optionala).map((l) => ({ id: l.id, denumire: l.articol.denumire, fel: "piesă" })),
    ],
    [reteta],
  );
  const [bifate, setBifate] = useState([]);

  return (
    <div className="modal-fundal" onClick={laRenuntare}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{tr(reteta.denumire)}</h2>
        <p className="subtitlu">
          {tr("Rețeta are linii opționale. Vin debifate — bifează doar ce ai folosit efectiv. Restul nu se pierde: rămâne în rețetă pentru data viitoare.")}
        </p>
        {optionale.map((o) => (
          <label key={o.id} className="linie-bifa">
            <input
              type="checkbox" checked={bifate.includes(o.id)}
              onChange={(e) => setBifate(e.target.checked
                ? [...bifate, o.id]
                : bifate.filter((x) => x !== o.id))}
            />
            {tr(o.denumire)} <span className="rezultat-context">({o.fel})</span>
          </label>
        ))}
        <div className="modal-actiuni">
          <button className="buton buton-mic buton-sters" onClick={laRenuntare}>{tr("renunță")}</button>
          <button className="buton buton-mic buton-actiune" onClick={() => laAplicare(bifate)}>
            {tr("aplică rețeta")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Pasul 4: materiale ──────────────────────────────────────────────────────

function PasMateriale({ fisa, editabila, fa, lucreaza, utilizatorId }) {
  const [articole, articoleDinTelefon] = useNomenclator("articole", () => api.articoleLista(), utilizatorId);
  const [gestiuni] = useNomenclator("gestiuni", () => api.gestiuni(), utilizatorId);
  const [d, setD] = useState({ articolId: "", cantitate: "", serie: "", gestiuneId: "" });

  const ales = articole.find((a) => a.id === d.articolId);
  const cereSerie = !!ales?.necesitaSerie;

  return (
    <div className="panou">
      <div className="panou-antet">
        <span>{tr("Piese și consumabile")} <span className="numar">{fisa.articole?.length ?? 0}</span></span>
      </div>
      <div className="panou-corp">
        {articoleDinTelefon && (
          <p className="nota">{tr("Lista vine din telefon, copiată la ultima trecere cu semnal. Ce e adăugat de atunci în birou nu se vede aici.")}</p>
        )}
        <p className="nota-cautare" style={{ marginTop: 0, marginBottom: ".8rem" }}>
          {tr("Gestiunea spune de unde pleacă piesa — stocul se descarcă abia la validarea fișei, nu acum. Fără gestiune, piesa se consideră cumpărată pe loc și nu scade niciun stoc.")}
        </p>

        {(fisa.articole ?? []).length === 0 ? (
          <div className="gol">{tr("Nicio piesă trecută pe fișă.")}</div>
        ) : (
          <table className="tabel">
            <thead>
              <tr>
                <th>{tr("Articol")}</th><th>{tr("Cantitate")}</th><th>{tr("Serie")}</th><th>{tr("Din gestiune")}</th>
                {fisa.articole.some((a) => a.pretUnitarVanzare !== undefined) && <th>{tr("Preț unitar")}</th>}
                {editabila && <th />}
              </tr>
            </thead>
            <tbody>
              {fisa.articole.map((a) => (
                <tr key={a.id}>
                  <td>
                    {tr(a.articol?.denumire)}
                    <span className="cod-mic" style={{ marginLeft: ".4rem" }}>{a.articol?.cod}</span>
                  </td>
                  <td>{Number(a.cantitate)} {um(a.articol?.unitateMasura)}</td>
                  <td>{a.serie || <span className="fara-pret">—</span>}</td>
                  <td>{a.gestiune?.denumire || <span className="fara-pret">{tr("cumpărată pe loc")}</span>}</td>
                  {fisa.articole.some((x) => x.pretUnitarVanzare !== undefined) && (
                    <td>{a.pretUnitarVanzare !== undefined ? lei(a.pretUnitarVanzare) : <span className="fara-pret">—</span>}</td>
                  )}
                  {editabila && (
                    <td className="celula-actiuni">
                      <button
                        className="buton buton-mic buton-sters" disabled={lucreaza}
                        onClick={() => fa(
                          () => api.stergeLinieFisa(fisa.id, "articole", a.id),
                          null,
                          { articole: articoleCoada(fisa).filter((_, i) => fisa.articole[i].id !== a.id) },
                        )}
                      >
                        {tr("scoate")}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {editabila && (
          <>
            <div className="formular-linie" style={{ gridTemplateColumns: "2fr 1fr 1fr", marginTop: "1rem" }}>
              <div className="camp" style={{ margin: 0 }}>
                <label htmlFor="m-articol">{tr("Articol")}</label>
                <select
                  id="m-articol" value={d.articolId}
                  onChange={(e) => setD({ ...d, articolId: e.target.value, serie: "" })}
                >
                  <option value="">{tr("alege…")}</option>
                  {articole.map((a) => (
                    <option key={a.id} value={a.id}>{a.cod} — {tr(a.denumire)}</option>
                  ))}
                </select>
              </div>
              <div className="camp" style={{ margin: 0 }}>
                <label htmlFor="m-cantitate">{tr("Cantitate")}</label>
                <input
                  id="m-cantitate" type="number" step="0.0001" min="0.0001" value={d.cantitate}
                  onChange={(e) => setD({ ...d, cantitate: e.target.value })}
                />
              </div>
              <div className="camp" style={{ margin: 0 }}>
                <label htmlFor="m-gestiune">{tr("Din gestiune")}</label>
                <select id="m-gestiune" value={d.gestiuneId} onChange={(e) => setD({ ...d, gestiuneId: e.target.value })}>
                  <option value="">{tr("cumpărată pe loc")}</option>
                  {gestiuni.map((g) => <option key={g.id} value={g.id}>{tr(g.denumire)}</option>)}
                </select>
              </div>
            </div>

            {cereSerie && (
              <div className="camp" style={{ marginTop: ".8rem" }}>
                <label htmlFor="m-serie">{tr("Serie (obligatorie pentru acest articol)")}</label>
                <input
                  id="m-serie" value={d.serie} placeholder={tr("citește-o de pe echipament")}
                  onChange={(e) => setD({ ...d, serie: e.target.value })}
                />
              </div>
            )}

            <div className="linie-actiune">
              <button
                className="buton buton-mic buton-actiune"
                disabled={!d.articolId || !d.cantitate || (cereSerie && !d.serie) || lucreaza}
                onClick={async () => {
                  const linie = {
                    articolId: d.articolId,
                    cantitate: Number(d.cantitate),
                    ...(d.serie ? { serie: d.serie } : {}),
                    ...(d.gestiuneId ? { gestiuneId: d.gestiuneId } : {}),
                  };
                  await fa(
                    () => api.adaugaArticolFisa(fisa.id, linie),
                    null,
                    { articole: [...articoleCoada(fisa), linie] },
                  );
                  setD({ articolId: "", cantitate: "", serie: "", gestiuneId: "" });
                }}
              >
                {tr("adaugă piesa")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Pasul 5: închidere și semnătură ─────────────────────────────────────────

function PasInchidere({
  fisa, editabila, fa, lucreaza, poateVedeaDeviz, poateStorna,
  estimariRamase, laSchimbareFisa, faraSemnal,
}) {
  const [stare, setStare] = useState(fisa.stareLaPlecare ?? "");
  const [necesitaRevenire, setNecesitaRevenire] = useState(!!fisa.necesitaRevenire);
  const [motivRevenire, setMotivRevenire] = useState(fisa.motivRevenire ?? "");
  const [semnatar, setSemnatar] = useState(fisa.numeSemnatarClient ?? "");
  const [deviz, setDeviz] = useState(null);
  const [erDeviz, setErDeviz] = useState("");
  const [formStorno, setFormStorno] = useState(false);
  const [formReteta, setFormReteta] = useState(false);
  const [respinge, setRespinge] = useState(false);
  const [motivRespingere, setMotivRespingere] = useState("");
  /*
   * Dreptul se ÎNTREABĂ, nu se deduce dintr-o listă de roluri scrisă aici:
   * firma îl poate muta pe oricare funcție, iar butonul trebuie să se mute
   * odată cu el. `null` = „încă nu știu", și NU e „am voie".
   */
  const [poateAproba, setPoateAproba] = useState(null);

  useEffect(() => {
    api.drepturileMele()
      .then((d) => setPoateAproba((d?.deviz ?? []).includes("aprobare")))
      .catch(() => setPoateAproba(false));
  }, []);

  useEffect(() => {
    if (!poateVedeaDeviz) { setDeviz(null); return; }
    api.deviz(fisa.id)
      .then((r) => { setDeviz(r); setErDeviz(""); })
      .catch((e) => { setDeviz(null); setErDeviz(mesajEroare(e)); });
  }, [fisa, poateVedeaDeviz]);

  const lipsuri = lipsuriFisa(fisa, estimariRamase, stare).map((x) => x.text);

  /*
   * Fără semnal, statusul de pe ecran rămâne cel dinainte: „validează fișa" n-a
   * ajuns la server, deci fișa arată tot ca ciornă. Dacă am cere `Validata`,
   * tehnicianul n-ar putea NICIODATĂ semna la obiectiv — adică exact rostul
   * modulului. Restul condițiilor rămân neatinse: fișa trebuie să fie completă.
   */
  const semnaturaFaraSemnal =
    faraSemnal && fisa.status === "Ciorna" && editabila && lipsuri.length === 0;
  const poateSemna = fisa.status === "Validata" || semnaturaFaraSemnal;

  return (
    <>
      <div className="panou">
        <div className="panou-antet"><span>{tr("Starea la plecare")}</span></div>
        <div className="panou-corp">
          <div className="camp">
            <label htmlFor="i-stare">{tr("Cum rămâne instalația")}</label>
            <select id="i-stare" value={stare} disabled={!editabila} onChange={(e) => setStare(e.target.value)}>
              <option value="">{tr("alege…")}</option>
              {STARI_PLECARE.map((s) => <option key={s.cod} value={s.cod}>{tr(s.titlu)}</option>)}
            </select>
          </div>
          <label className="linie-bifa">
            <input
              type="checkbox" checked={necesitaRevenire} disabled={!editabila}
              onChange={(e) => setNecesitaRevenire(e.target.checked)}
            />
            {tr("necesită revenire")}
          </label>
          {necesitaRevenire && (
            <div className="camp">
              <label htmlFor="i-motiv">{tr("De ce se revine")}</label>
              <input
                id="i-motiv" value={motivRevenire} disabled={!editabila}
                placeholder={tr("ex. piesa comandată ajunge săptămâna viitoare")}
                onChange={(e) => setMotivRevenire(e.target.value)}
              />
            </div>
          )}
          {editabila && (
            <div className="linie-actiune">
              <button
                className="buton buton-mic buton-actiune" disabled={lucreaza || !stare}
                onClick={() => {
                  const dto = {
                    stareLaPlecare: stare,
                    necesitaRevenire,
                    ...(necesitaRevenire && motivRevenire ? { motivRevenire } : {}),
                  };
                  return fa(() => api.continutFisa(fisa.id, dto), null, dto);
                }}
              >
                {tr("salvează starea")}
              </button>
            </div>
          )}
        </div>
      </div>

      {poateVedeaDeviz && (
        <div className="panou">
          <div className="panou-antet">
            <span>{tr("Deviz")}</span>
            {fisa.status === "Ciorna" && <span className="marcaj">{tr("estimativ — prețurile se îngheață la validare")}</span>}
          </div>
          <div className="panou-corp">
            {erDeviz ? (
              <div className="gol">{erDeviz}</div>
            ) : !deviz ? (
              <div className="gol">{tr("Se calculează…")}</div>
            ) : (
              <Deviz deviz={deviz.deviz} contract={deviz.contract} />
            )}
          </div>
        </div>
      )}

      {!poateVedeaDeviz && (
        <div className="panou">
          <div className="panou-corp gol">
            {tr("Rolul tău vede lucrarea — operații, piese, cantități — dar nu și sumele.")}
          </div>
        </div>
      )}

      <div className="panou">
        <div className="panou-antet"><span>{tr("Închidere")}</span></div>
        <div className="panou-corp">
          {fisa.status === "Ciorna" && (
            <>
              {lipsuri.length > 0 ? (
                <div className="panou-corp gol" style={{ padding: 0, marginBottom: ".8rem" }}>
                  {tr("Mai lipsesc:")} {lipsuri.join(", ")}.
                </div>
              ) : (
                <p className="nota-cautare" style={{ marginTop: 0 }}>
                  {tr("La validare se descarcă stocul, echipamentele montate intră în parc, cele înlocuite ies din funcțiune, iar prețurile se îngheață pe linii. Totul odată, sau deloc.")}
                </p>
              )}
              {editabila && (
                <div className="linie-actiune">
                  <button
                    className="buton buton-mic buton-actiune"
                    disabled={lucreaza || lipsuri.length > 0}
                    onClick={() => fa(() => api.finalizeazaFisa(fisa.id))}
                  >
                    {tr("validează fișa")}
                  </button>
                </div>
              )}
            </>
          )}

          {fisa.status === "AsteaptaAprobare" && (
            <>
              <p className="nota-cautare" style={{ marginTop: 0 }}>
                {tr("Devizul așteaptă aprobarea cuiva cu dreptul. Fișa e completă și înghețată — nimic nu s-a întâmplat încă: stocul nu s-a scăzut, echipamentele n-au intrat în parc, lucrarea nu se poate preda la facturare.")}
              </p>
              {poateAproba === true ? (
                <div className="linie-actiune">
                  <button
                    className="buton buton-mic buton-actiune" disabled={lucreaza}
                    onClick={() => fa(() => api.aprobaDeviz(fisa.id))}
                  >
                    {tr("aprobă devizul și validează fișa")}
                  </button>
                  <button
                    className="buton buton-mic buton-sters" disabled={lucreaza}
                    onClick={() => { setRespinge(true); setMotivRespingere(""); }}
                  >
                    {tr("respinge")}
                  </button>
                </div>
              ) : (
                <p className="nota">
                  {tr("Funcția ta nu aprobă devize. A fost anunțat cine o poate face.")}
                </p>
              )}
              {fisa.motivRespingereDeviz && (
                <p className="nota">{tr("Respins înainte:")} {fisa.motivRespingereDeviz}</p>
              )}
            </>
          )}

          {fisa.status === "Ciorna" && fisa.motivRespingereDeviz && (
            <p className="nota">
              {tr("Devizul a fost respins:")} {fisa.motivRespingereDeviz}
            </p>
          )}

          {poateSemna && (
            <>
              {/* MT19 — fără semnal, validarea n-are cum să se întâmple aici:
                  ea descarcă stocul și îngheață prețurile, adică lucruri pe
                  care numai serverul le poate face. Semnătura se ia totuși
                  ACUM, la obiectiv, fiindcă omul e în fața clientului, iar
                  urcarea validează întâi și abia apoi semnează — cu exact
                  aceleași verificări ca o fișă completată în birou. */}
              {semnaturaFaraSemnal && (
                <p className="nota">
                  {tr("Nu e semnal. Semnătura se ia acum și pleacă odată cu fișa; validarea o face serverul la urcare, cu toate verificările ei.")}
                </p>
              )}
              <div className="camp">
                <label htmlFor="i-semnatar">{tr("Cine semnează din partea clientului")}</label>
                <input
                  id="i-semnatar" value={semnatar} placeholder={tr("nume și prenume")}
                  onChange={(e) => setSemnatar(e.target.value)}
                />
              </div>
              <div className="linie-actiune">
                <button
                  className="buton buton-mic buton-actiune"
                  disabled={lucreaza || semnatar.trim().length < 3}
                  onClick={() => {
                    const numeSemnatarClient = semnatar.trim();
                    return fa(
                      () => api.semneazaFisa(fisa.id, { numeSemnatarClient }),
                      null,
                      {
                        numeSemnatarClient,
                        // Momentul e cel al TELEFONULUI; serverul îl compară cu
                        // ceasul lui și consemnează devierea, nu-l crede orbește.
                        momentSemnaturii: new Date().toISOString(),
                        /*
                         * Starea la plecare pleacă ODATĂ cu semnătura.
                         *
                         * Fără semnal, „salvează starea" n-a ajuns nicăieri, iar
                         * alegerea trăia doar în ecran. Urcarea validează întâi
                         * fișa, iar validarea CERE starea la plecare — deci fără
                         * ea fișa semnată în hală ar fi fost respinsă la
                         * sincronizare, cu munca blocată în telefon.
                         */
                        ...(stare ? {
                          stareLaPlecare: stare,
                          necesitaRevenire,
                          ...(necesitaRevenire && motivRevenire ? { motivRevenire } : {}),
                        } : {}),
                      },
                    );
                  }}
                >
                  {tr("semnează")}
                </button>
              </div>
            </>
          )}

          {fisa.status === "Semnata" && (
            <>
              <p className="nota-cautare" style={{ marginTop: 0 }}>
                {tr("Semnată de")} <strong>{fisa.numeSemnatarClient}</strong> {tr("la")} {ora(fisa.dataSemnare)}{tr(", pe")} {data(fisa.dataSemnare)}.
              </p>
              <div className="linie-actiune">
                <button className="buton buton-mic buton-sters" onClick={() => setFormReteta(true)}>
                  {tr("salvează ca rețetă")}
                </button>
                {poateStorna && (
                  <button className="buton buton-mic buton-sters" onClick={() => setFormStorno(true)}>
                    {tr("stornează")}
                  </button>
                )}
              </div>
            </>
          )}

          {fisa.status === "Stornata" && (
            <p className="nota-cautare" style={{ marginTop: 0 }}>
              {tr("Stornată:")} {fisa.motivStornare}
              {fisa.stornataDeId && (
                <>
                  {" — "}
                  <button className="buton buton-mic buton-sters" onClick={() => laSchimbareFisa(fisa.stornataDeId)}>
                    {tr("deschide fișa care o înlocuiește")}
                  </button>
                </>
              )}
            </p>
          )}
        </div>
      </div>

      {respinge && (
        <div className="modal-fundal" onClick={() => setRespinge(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{tr("Respingi devizul")}</h3>
            <p className="subtitlu">
              {tr("Fișa se întoarce în ciornă și se poate corecta. Cine a cerut aprobarea e anunțat cu motivul.")}
            </p>
            <div className="camp">
              <label htmlFor="i-motiv-deviz">{tr("De ce")}</label>
              <textarea
                id="i-motiv-deviz" rows={2} value={motivRespingere}
                placeholder={tr("ex. manopera e prea mare față de lucrarea făcută")}
                onChange={(e) => setMotivRespingere(e.target.value)}
              />
            </div>
            <div className="modal-actiuni">
              <button className="buton buton-sters" onClick={() => setRespinge(false)}>
                {tr("renunță")}
              </button>
              <button
                className="buton buton-actiune"
                disabled={lucreaza || motivRespingere.trim().length < 5}
                onClick={async () => {
                  await fa(() => api.respingeDeviz(fisa.id, motivRespingere.trim()));
                  setRespinge(false); setMotivRespingere("");
                }}
              >
                {tr("trimite respingerea")}
              </button>
            </div>
          </div>
        </div>
      )}

      {formStorno && (
        <FormStorno
          laRenuntare={() => setFormStorno(false)}
          laSalvare={async (motiv) => {
            const r = await fa(() => api.storneazaFisa(fisa.id, motiv));
            setFormStorno(false);
            if (r?.fisaNoua?.id) laSchimbareFisa(r.fisaNoua.id);
          }}
        />
      )}

      {formReteta && (
        <FormRetetaDinFisa
          fisa={fisa}
          laRenuntare={() => setFormReteta(false)}
          laSalvare={async (dto) => { await fa(() => api.retetaDinFisa(fisa.id, dto)); setFormReteta(false); }}
        />
      )}
    </>
  );
}

function Deviz({ deviz, contract }) {
  const grupe = [
    ["Materiale", deviz.materiale],
    ["Manoperă", deviz.manopera],
    ["Deplasare", deviz.deplasare],
  ].filter(([, linii]) => linii.length > 0);

  return (
    <>
      {contract && (
        <p className="nota-cautare" style={{ marginTop: 0 }}>
          {tr("Contract")} <span className="cod-mic">{contract.numar}</span> {tr("— include")} {enumera([
            contract.includeManopera && "manopera",
            contract.includeMateriale && "materialele",
            contract.includeDeplasare && "deplasarea",
          ].filter(Boolean)) || tr("nimic (totul se facturează)")}.
        </p>
      )}

      {grupe.map(([titlu, linii]) => (
        <div key={titlu} style={{ marginBottom: "1rem" }}>
          <div className="subsistem-titlu">{titlu}</div>
          <table className="tabel">
            <thead>
              <tr><th>{tr("Denumire")}</th><th>{tr("Cantitate")}</th><th>{tr("Preț unitar")}</th><th>{tr("Valoare")}</th><th>{tr("Acoperire")}</th></tr>
            </thead>
            <tbody>
              {linii.map((l, i) => (
                <tr key={i}>
                  <td>{tr(l.denumire)}</td>
                  <td>{l.cantitate}</td>
                  <td>{lei(l.pretUnitar)}</td>
                  <td>{l.modAcoperire === "Facturabil" ? lei(l.valoare) : <span className="fara-pret">{tr("0,00 lei")}</span>}</td>
                  <td><span className="pastila pastila-soft">{eticheta(ETICHETE_ACOPERIRE, l.modAcoperire)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <table className="tabel">
        <tbody>
          <tr><td>{tr("Subtotal")}</td><td style={{ textAlign: "right" }}>{lei(deviz.subtotal)}</td></tr>
          {deviz.discount > 0 && (
            <tr><td>{tr("Discount")}</td><td style={{ textAlign: "right" }}>−{lei(deviz.discount)}</td></tr>
          )}
          <tr><td>{tr("Total fără TVA")}</td><td style={{ textAlign: "right" }}>{lei(deviz.totalFaraTVA)}</td></tr>
          <tr><td>TVA</td><td style={{ textAlign: "right" }}>{lei(deviz.tva)}</td></tr>
          <tr><td><strong>{tr("Total")}</strong></td><td style={{ textAlign: "right" }}><strong>{lei(deviz.total)}</strong></td></tr>
        </tbody>
      </table>

      {deviz.intern && (
        <div style={{ marginTop: "1rem" }}>
          <div className="subsistem-titlu">{tr("Intern — nu se arată clientului")}</div>
          <table className="tabel">
            <tbody>
              <tr><td>{tr("Cost materiale")}</td><td style={{ textAlign: "right" }}>{lei(deviz.intern.costMateriale)}</td></tr>
              <tr><td>{tr("Cost manoperă (pe timpul real)")}</td><td style={{ textAlign: "right" }}>{lei(deviz.intern.costManopera)}</td></tr>
              {deviz.intern.costDeplasare > 0 && (
                <tr><td>{tr("Cost deplasare")}</td><td style={{ textAlign: "right" }}>{lei(deviz.intern.costDeplasare)}</td></tr>
              )}
              <tr><td>{tr("Cost total")}</td><td style={{ textAlign: "right" }}>{lei(deviz.intern.costTotal)}</td></tr>
              <tr>
                <td><strong>{tr("Marjă")}</strong></td>
                <td style={{ textAlign: "right" }}>
                  <strong>{lei(deviz.intern.marja)}</strong>{" "}
                  <span className="rezultat-context">
                    ({Number(deviz.intern.marjaProcent).toLocaleString("ro-RO", { maximumFractionDigits: 2 })}%)
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function FormStorno({ laRenuntare, laSalvare }) {
  const [motiv, setMotiv] = useState("");
  return (
    <div className="modal-fundal" onClick={laRenuntare}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{tr("Stornează fișa")}</h2>
        <p className="subtitlu">
          {tr("Fișa semnată rămâne în bază, marcată ca stornată. În locul ei se emite una nouă, cu aceleași linii, pe care o poți corecta.")}
        </p>
        <div className="camp">
          <label htmlFor="s-motiv">{tr("Motivul stornării")}</label>
          <textarea
            id="s-motiv" rows={3} className="camp-textarea" value={motiv} minLength={10}
            placeholder={tr("Cel puțin 10 caractere — se vede în audit.")}
            onChange={(e) => setMotiv(e.target.value)}
          />
        </div>
        <div className="modal-actiuni">
          <button className="buton buton-mic buton-sters" onClick={laRenuntare}>{tr("renunță")}</button>
          <button
            className="buton buton-mic buton-actiune" disabled={motiv.trim().length < 10}
            onClick={() => laSalvare(motiv)}
          >
            {tr("stornează")}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormRetetaDinFisa({ fisa, laRenuntare, laSalvare }) {
  const [d, setD] = useState({ cod: "", denumire: "", descriere: "" });
  const [optOp, setOptOp] = useState([]);
  const [optArt, setOptArt] = useState([]);

  const comuta = (lista, pune, id) => pune(lista.includes(id) ? lista.filter((x) => x !== id) : [...lista, id]);

  return (
    <div className="modal-fundal" onClick={laRenuntare}>
      <div className="modal modal-lat" onClick={(e) => e.stopPropagation()}>
        <h2>{tr("Salvează lucrarea ca rețetă")}</h2>
        <p className="subtitlu">
          {tr("Biblioteca se populează din munca reală, nu dintr-o lună de analiză la birou. Bifează ce nu se repetă la fiecare intervenție — acele linii vor veni data viitoare debifate.")}
        </p>
        <div className="formular-linie" style={{ gridTemplateColumns: "1fr 2fr" }}>
          <div className="camp">
            <label htmlFor="r-cod">{tr("Cod")}</label>
            <input id="r-cod" value={d.cod} placeholder={tr("ex. CAM-EXT-01")} onChange={(e) => setD({ ...d, cod: e.target.value })} />
          </div>
          <div className="camp">
            <label htmlFor="r-denumire">{tr("Denumire")}</label>
            <input
              id="r-denumire" value={d.denumire} placeholder={tr("ex. Înlocuire cameră IP fixă exterior")}
              onChange={(e) => setD({ ...d, denumire: e.target.value })}
            />
          </div>
        </div>

        <div className="subsistem-titlu">{tr("Operații")}</div>
        {(fisa.operatii ?? []).map((o) => (
          <label key={o.id} className="linie-bifa">
            <input type="checkbox" checked={optOp.includes(o.id)} onChange={() => comuta(optOp, setOptOp, o.id)} />
            {tr(o.operatie?.denumire)} <span className="rezultat-context">{tr("opțională")}</span>
          </label>
        ))}

        {(fisa.articole ?? []).length > 0 && (
          <>
            <div className="subsistem-titlu">{tr("Piese")}</div>
            {fisa.articole.map((a) => (
              <label key={a.id} className="linie-bifa">
                <input type="checkbox" checked={optArt.includes(a.id)} onChange={() => comuta(optArt, setOptArt, a.id)} />
                {tr(a.articol?.denumire)} <span className="rezultat-context">{tr("opțională")}</span>
              </label>
            ))}
          </>
        )}

        <div className="modal-actiuni">
          <button className="buton buton-mic buton-sters" onClick={laRenuntare}>{tr("renunță")}</button>
          <button
            className="buton buton-mic buton-actiune"
            disabled={!d.cod.trim() || d.denumire.trim().length < 3}
            onClick={() => laSalvare({
              cod: d.cod.trim(), denumire: d.denumire.trim(),
              ...(d.descriere ? { descriere: d.descriere } : {}),
              ...(optOp.length ? { optionaleOperatii: optOp } : {}),
              ...(optArt.length ? { optionaleArticole: optArt } : {}),
            })}
          >
            {tr("creează rețeta")}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Fotografiile de pe teren.
 *
 * Modelul, marcajele de pe ecran („foto înainte", „foto după") și cele 16
 * rânduri de checklist care CER o fotografie existau de la F3. Drumul de
 * scriere lipsea cu totul: aplicația îi cerea tehnicianului ceva ce nu putea
 * primi. Aici se închide.
 *
 * `capture="environment"` face ca pe telefon butonul să deschidă direct camera
 * din spate, nu galeria — pe teren asta e diferența dintre o poză făcută acum
 * și una căutată prin poze vechi. Pe desktop, atributul e ignorat și rămâne
 * alegerea obișnuită de fișier.
 *
 * Poza se micșorează ÎNAINTE de trimitere: un telefon de azi scoate 4-8 MB pe
 * cadru, iar pe o conexiune de la subsolul unei hale asta înseamnă un minut de
 * așteptare pentru ceva ce se citește la fel de bine la 1600px.
 */
function PasFotografii({ fisa, editabila, laSchimbare }) {
  const [lucreaza, setLucreaza] = useState(false);
  const [eroare, setEroare] = useState("");
  const [tip, setTip] = useState("Inainte");
  const [descriere, setDescriere] = useState("");
  const [previzualizari, setPrevizualizari] = useState({});

  const fotografii = fisa.fotografii ?? [];

  // Adresele locale se eliberează la demontare: fără asta, fiecare intrare pe
  // ecran ar lăsa în urmă un blob în memoria browserului.
  useEffect(() => {
    let vii = true;
    const create = [];
    (async () => {
      for (const f of fotografii) {
        if (!f.documentId || previzualizari[f.id]) continue;
        try {
          const url = await api.previzualizeazaDocument(f.documentId);
          create.push(url);
          if (vii) setPrevizualizari((p) => ({ ...p, [f.id]: url }));
        } catch { /* o fotografie care nu se încarcă nu strică ecranul */ }
      }
    })();
    return () => {
      vii = false;
      for (const u of create) URL.revokeObjectURL(u);
    };
  }, [fisa.id, fotografii.length]);

  async function micsoreaza(fisier) {
    const bitmap = await createImageBitmap(fisier);
    const latura = 1600;
    const scara = Math.min(1, latura / Math.max(bitmap.width, bitmap.height));
    const panza = document.createElement("canvas");
    panza.width = Math.round(bitmap.width * scara);
    panza.height = Math.round(bitmap.height * scara);
    panza.getContext("2d").drawImage(bitmap, 0, 0, panza.width, panza.height);
    return panza.toDataURL("image/jpeg", 0.82);
  }

  async function trimite(e) {
    const fisier = e.target.files?.[0];
    e.target.value = "";
    if (!fisier) return;
    setLucreaza(true); setEroare("");
    try {
      const base64 = await micsoreaza(fisier);
      await api.adaugaFotografieFisa(fisa.id, {
        tip,
        continutBase64: base64,
        mimeType: "image/jpeg",
        ...(descriere.trim() ? { descriere: descriere.trim() } : {}),
      });
      setDescriere("");
      await laSchimbare();
    } catch (er) { setEroare(mesajEroare(er)); }
    finally { setLucreaza(false); }
  }

  async function scoate(fotoId) {
    setLucreaza(true); setEroare("");
    try {
      await api.stergeFotografieFisa(fisa.id, fotoId);
      await laSchimbare();
    } catch (er) { setEroare(mesajEroare(er)); }
    finally { setLucreaza(false); }
  }

  return (
    <div className="panou">
      <div className="panou-antet">
        <span>{tr("Fotografii")} <span className="numar">{fotografii.length}</span></span>
      </div>
      <div className="panou-corp">
        {eroare && <div className="eroare">{eroare}</div>}

        {fotografii.length === 0 ? (
          <div className="gol">{tr("Nicio fotografie pe fișă.")}</div>
        ) : (
          <div className="galerie-foto">
            {fotografii.map((f) => (
              <figure key={f.id} className="foto-cadru">
                {previzualizari[f.id]
                  ? <img src={previzualizari[f.id]} alt={f.descriere ?? tr("fotografie de pe teren")} />
                  : <div className="gol">{tr("Se încarcă…")}</div>}
                <figcaption>
                  <span className="pastila pastila-soft">{eticheta(TIPURI_FOTO, f.tip)}</span>
                  {f.descriere && <span className="rezultat-context"> {tr(f.descriere)}</span>}
                  {editabila && (
                    <button className="buton buton-mic buton-discret"
                      disabled={lucreaza} onClick={() => scoate(f.id)}>
                      {tr("scoate")}
                    </button>
                  )}
                </figcaption>
              </figure>
            ))}
          </div>
        )}

        {editabila && (
          <>
            <div className="formular-linie" style={{ gridTemplateColumns: "1fr 2fr", marginTop: ".8rem" }}>
              <div className="camp" style={{ margin: 0 }}>
                <label htmlFor="f-tip">{tr("Fel")}</label>
                <select id="f-tip" value={tip} onChange={(e) => setTip(e.target.value)}>
                  {Object.entries(TIPURI_FOTO).map(([v, e]) => (
                    <option key={v} value={v}>{tr(e)}</option>
                  ))}
                </select>
              </div>
              <div className="camp" style={{ margin: 0 }}>
                <label htmlFor="f-desc">{tr("Ce se vede (opțional)")}</label>
                <input id="f-desc" value={descriere} maxLength={300}
                  placeholder={tr("ex. sursa arsă, înainte de înlocuire")}
                  onChange={(e) => setDescriere(e.target.value)} />
              </div>
            </div>
            <div className="linie-actiune">
              <label className="buton buton-mic buton-actiune" style={{ cursor: "pointer" }}>
                {lucreaza ? tr("se trimite…") : tr("fă o fotografie")}
                <input type="file" accept="image/*" capture="environment"
                  disabled={lucreaza} onChange={trimite} style={{ display: "none" }} />
              </label>
            </div>
            <p className="nota-cautare">
              {tr("Pe telefon se deschide direct camera. Poza se micșorează înainte de trimitere și intră în dosarul tehnic al clientului.")}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
